import { FC, useCallback, useEffect, useState, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "components/common/ui/PageHeader";
import { Container, Grid } from "@mui/material";
import useAuth from "hooks/useAuth";
import jspPlatformApi from "client/portals";
import { isApiError, toJspApiError } from "models/utils/apiUtils";
import EditSection from "components/common/ui/EditSection";
import CommonButton from "components/common/ui/CommonButton";
import SpacedTextInput from "components/common/ui/SpacedTextInput";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import userAccountSelector from "store/selectors/user/userAccountSelector";
import { userAccountActions } from "store/slices/user/userAccountSlice";

interface MyAccountPageProps {}

const MyAccountPage: FC<MyAccountPageProps> = () => {
  const { t } = useTranslation();

  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const dispatch = useAppDispatch();
  const auth = useAuth();
  const promptSnackBar = usePromptSnackBar();

  const userInfo = useAppSelector(userAccountSelector.userAccount);
  const isLoading = useAppSelector(userAccountSelector.userAccountIsLoading);
  const fullName = useAppSelector(userAccountSelector.userFullName);
  const userId = useAppSelector(userAccountSelector.userId);
  const ethnicity = useAppSelector(userAccountSelector.userEthnicity);
  const dateOfBirth = useAppSelector(userAccountSelector.userDateOfBirth);
  const gender = useAppSelector(userAccountSelector.userGender);
  const weight = useAppSelector(userAccountSelector.userWeight);
  const userGroups = useAppSelector(userAccountSelector.userGroups);

  /**
   * Load user account info if not already in Redux
   * This handles cases like page refresh where Redux might be empty
   */
  useEffect(() => {
    if (!userInfo && !isLoading) {
      if (auth.user) {
        // If we have user in auth context but not in Redux, set it
        dispatch(userAccountActions.setUserAccount(auth.user));
      } else {
        // Fetch user account if not available
        dispatch(userAccountActions.fetchUserAccount({ userId: 0 }));
      }
    }
  }, [auth, userInfo, isLoading, dispatch]);

  const handlePasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setNewPassword(event.target.value);
    },
    [],
  );

  const handleConfirmPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setConfirmPassword(event.target.value);
    },
    [],
  );

  const handleChangePasswordClick = useCallback(() => {
    if (!newPassword || !confirmPassword) {
      promptSnackBar.setPromptProps({
        isOpen: true,
        message: t("Please enter both new password and confirmation."),
        severity: "error",
        autoHeightDuration: 5000,
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      promptSnackBar.setPromptProps({
        isOpen: true,
        message: t("Passwords do not match."),
        severity: "error",
        autoHeightDuration: 5000,
      });
      return;
    }

    jspPlatformApi()
      .authentication.putAuthChangePassword({
        body: { new_password: newPassword },
      })
      .then(() => {
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: t("Password changed successfully."),
          severity: "success",
          autoHeightDuration: 5000,
        });
        setNewPassword("");
        setConfirmPassword("");
      })
      .catch((error) => {
        const err = isApiError(error) ? toJspApiError(error) : null;
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: t("Failed to change password: {{error}}", {
            error: err?.statusText ?? t("Unknown error"),
          }),
          severity: "error",
          autoHeightDuration: 5000,
        });
      });
  }, [newPassword, confirmPassword, promptSnackBar, t]);

  const handleLogout = useCallback(() => {
    auth.signOut(() => {});
  }, [auth]);

  return (
    <Container>
      <PageHeader>{t("My Account")}</PageHeader>
      {isLoading ? (
        <div>{t("Loading...")}</div>
      ) : (
        <>
          <EditSection header={t("Account Details")}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <div>
                  <strong>{t("Full Name:")}</strong> {fullName || t("N/A")}
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <div>
                  <strong>{t("User ID:")}</strong> {userId || t("N/A")}
                </div>
              </Grid>
              {ethnicity && (
                <Grid item xs={12} sm={6}>
                  <div>
                    <strong>{t("Ethnicity:")}</strong> {ethnicity}
                  </div>
                </Grid>
              )}
              {dateOfBirth && (
                <Grid item xs={12} sm={6}>
                  <div>
                    <strong>{t("Date of Birth:")}</strong> {dateOfBirth}
                  </div>
                </Grid>
              )}
              {gender && (
                <Grid item xs={12} sm={6}>
                  <div>
                    <strong>{t("Gender:")}</strong> {gender}
                  </div>
                </Grid>
              )}
              {weight !== null && (
                <Grid item xs={12} sm={6}>
                  <div>
                    <strong>{t("Weight:")}</strong>{" "}
                    {t("{{weight}} kg", { weight })}
                  </div>
                </Grid>
              )}
              {userGroups && userGroups.length > 0 && (
                <Grid item xs={12}>
                  <div>
                    <strong>{t("User Groups:")}</strong>{" "}
                    {userGroups
                      .map(
                        (group) =>
                          group.group_name ||
                          t("Group ID: {{id}}", { id: group.id }),
                      )
                      .join(", ")}
                  </div>
                </Grid>
              )}
            </Grid>
          </EditSection>

          <EditSection header={t("Actions")}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <h3>{t("Change Password")}</h3>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SpacedTextInput
                  label={t("New Password")}
                  type="password"
                  value={newPassword}
                  onChange={handlePasswordChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SpacedTextInput
                  label={t("Confirm Password")}
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  fullWidth
                  InputLabelProps={{
                    shrink:
                      confirmPassword !== "" || confirmPassword !== newPassword,
                  }}
                  error={confirmPassword !== newPassword}
                  helperText={
                    confirmPassword !== newPassword
                      ? t("Passwords do not match.")
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <CommonButton
                  text={t("Change Password")}
                  positive
                  onClick={handleChangePasswordClick}
                />
              </Grid>
              <Grid item xs={12}>
                <CommonButton
                  text={t("Logout")}
                  danger
                  onClick={handleLogout}
                />
              </Grid>
            </Grid>
          </EditSection>
        </>
      )}
    </Container>
  );
};

export default MyAccountPage;
